<template>
  <div class="text-gray-700">
    <div class="flex items-center justify-between">
      <button
        class="mb-3 flex items-center gap-2 rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-700 md:mb-5 md:text-sm"
        @click="goBack"
      >
        <ChevronLeftIcon class="size-3 md:size-4" />
        <span>Volver</span>
      </button>
    </div>
    <div class="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:p-6">
      <h2 class="mb-4 flex items-center justify-between text-lg font-semibold text-gray-900 md:text-xl">
        {{ discount.name }}
        <span
          class="rounded-full px-2 py-1 text-xs uppercase"
          :class="{
            'bg-green-100 text-green-800': !isExpired,
            'bg-red-100 text-red-800': isExpired,
          }"
        >
          {{ isExpired ? 'Vencido' : 'Activo' }}
        </span>
      </h2>
      <div class="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
        <div class="rounded-md bg-slate-50 p-3 md:p-4">
          <dt class="text-xs font-medium text-slate-600 md:text-sm">Descuento total</dt>
          <dd class="mt-1 text-base font-semibold text-slate-900 md:text-lg">{{ discount.totalDiscount }}%</dd>
        </div>
        <div class="rounded-md bg-gray-50 p-3 md:p-4">
          <dt class="text-xs font-medium text-gray-500 md:text-sm">Descuento de marca</dt>
          <dd class="mt-1 text-base font-semibold text-gray-900 md:text-lg">{{ discount.makerDiscount }}%</dd>
        </div>
        <div class="rounded-md bg-gray-50 p-3 md:p-4">
          <dt class="text-xs font-medium text-gray-500 md:text-sm">Descuento Lokal</dt>
          <dd class="mt-1 text-base font-semibold text-gray-900 md:text-lg">{{ discount.lokalDiscount }}%</dd>
        </div>
      </div>
    </div>
    <lokal-loading
      v-if="discountProductsIsLoading"
      :loading="discountProductsIsLoading"
    />
    <div v-else>
      <h3 class="mb-4 text-lg font-semibold text-gray-900 md:text-xl">Productos con descuento</h3>
      <div
        v-if="paginationInfo"
        class="mb-4 text-sm text-gray-500 md:text-base"
      >
        {{ paginationInfo }}
      </div>
      <div
        v-if="!discountProducts || discountProducts.length === 0"
        class="py-8 text-center text-gray-500"
      >
        No hay productos asociados a este descuento.
      </div>
      <div v-else>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="product in discountProducts"
            :key="product.id"
            class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div class="flex items-start gap-3">
              <img
                v-if="product.images?.[0]?.derivatives?.jpgSm?.url"
                :src="product.images[0].derivatives.jpgSm.url"
                :alt="product.name"
                class="size-16 rounded-md object-cover"
              />
              <div
                v-else
                class="flex size-16 items-center justify-center rounded-md bg-gray-100"
              >
                <span class="text-xs text-gray-400">Sin imagen</span>
              </div>
              <div class="flex-1">
                <h4 class="line-clamp-2 text-sm font-medium text-gray-900">
                  {{ product.name }}
                </h4>
                <p class="mt-1 text-xs text-gray-500">SKU: {{ product.sku }}</p>
                <p class="mt-1 text-xs text-gray-500">
                  Precio mayorista: {{ useFormatCurrency(product.wholesalePriceCents) }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <pagination-component
          v-if="totalPages && totalPages > 1"
          :total-pages="totalPages"
          :current-page="currentPage"
          @set-new-page="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/solid';
import PaginationComponent from './shared/pagination-component.vue';
import useDiscountProductsQuery from './queries/discount-products-query';
import useFormatCurrency from './use/format-currency';

const props = defineProps<{
  discount: Discount;
}>();

const emit = defineEmits<{
  'go-back': [];
}>();

const PER_PAGE = 12;

const currentPage = ref(1);
const discountId = computed(() => props.discount.id);

const { discountProductsIsLoading, discountProducts, totalResources, resourcesPerPage, totalPages } =
  useDiscountProductsQuery(
    discountId,
    computed(() => currentPage.value),
    computed(() => PER_PAGE),
  );

const isExpired = computed(() => {
  const endDate = new Date(props.discount.endDate);
  return endDate < new Date();
});

const paginationInfo = computed(() => {
  if (!totalResources.value || totalResources.value === 0) {
    return null;
  }
  const startItem = (currentPage.value - 1) * resourcesPerPage.value + 1;
  const endItem = Math.min(currentPage.value * resourcesPerPage.value, totalResources.value);

  return `Mostrando ${startItem} - ${endItem} de ${totalResources.value} productos`;
});

function goBack() {
  emit('go-back');
}

function handlePageChange(page: number) {
  currentPage.value = page;
}

watch(
  () => props.discount.id,
  () => {
    currentPage.value = 1;
  },
);

watch(
  totalPages,
  (newTotalPages) => {
    if (newTotalPages && currentPage.value > newTotalPages && newTotalPages > 0) {
      currentPage.value = newTotalPages;
    }
  },
  { immediate: true },
);
</script>
